import {
  formatBRL,
  KANBAN_COLUMNS,
  ORDER_STATUS_LABELS,
  type OrderStatus,
} from '@centerpanos/types';
import { Button, cn } from '@centerpanos/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  MessageCircle,
  Plus,
  RefreshCw,
  Trash2,
} from 'lucide-react';
import { useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { PageHeader } from '../components/layout';
import {
  Card,
  EmptyState,
  Field,
  Input,
  Modal,
  Pagination,
  Select,
  Spinner,
  Textarea,
} from '../components/ui';
import { api, type DeliveryMethodOption, type OrderCard } from '../lib/api';
import { formatDateTime } from '../lib/format';
import { useAuth } from '../lib/auth';
import { useToast } from '../lib/toast';

/**
 * Kanban de pedidos.
 *
 * **Por que atualização por intervalo, e não por conexão aberta.** O quadro
 * precisa mostrar o pedido que acabou de chegar sem ninguém recarregar a
 * página. SSE ou WebSocket dariam isso, mas o painel autentica com um token
 * no cabeçalho `Authorization`, e o `EventSource` do navegador não envia
 * cabeçalho — a saída seria pôr o token na URL, onde ele vaza para log de
 * proxy e histórico. Somando isso ao servidor de 1 vCPU compartilhado com
 * outros seis sites, uma consulta a cada dez segundos com duas atendentes
 * abertas é mais barata e muito mais simples do que manter conexões vivas.
 */
const INTERVALO_MS = 10_000;
const MAX_CARDS_COLAPSADO = 3;

/** Colunas cujo status exige motivo de perda. */
const COLUNAS_COM_MOTIVO = new Set(['perdido', 'cancelado']);

export function OrdersBoardPage() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { can } = useAuth();

  const quadro = useQuery({
    queryKey: ['pedidos', 'kanban'],
    queryFn: api.pedidos.kanban,
    refetchInterval: INTERVALO_MS,
    refetchOnWindowFocus: true,
  });

  const recuperacao = useQuery({
    queryKey: ['pedidos', 'recuperacao'],
    queryFn: api.pedidos.recuperacao,
    refetchInterval: INTERVALO_MS * 6,
  });

  const [arrastando, setArrastando] = useState<string | null>(null);
  const [colunaAlvo, setColunaAlvo] = useState<string | null>(null);

  // Colunas expandidas além de MAX_CARDS_COLAPSADO.
  const [colunasExpandidas, setColunasExpandidas] = useState<Set<string>>(new Set());

  function toggleColuna(key: string) {
    setColunasExpandidas((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  // Scroll horizontal com o mouse pressionado.
  const scrollRef = useRef<HTMLDivElement>(null);
  const scrollDrag = useRef({ ativo: false, startX: 0, scrollLeft: 0 });
  const [scrollDragAtivo, setScrollDragAtivo] = useState(false);

  function iniciarScrollDrag(e: React.MouseEvent<HTMLDivElement>) {
    if (arrastando) return; // card DnD tem prioridade
    const el = scrollRef.current;
    if (!el) return;
    scrollDrag.current = { ativo: true, startX: e.pageX, scrollLeft: el.scrollLeft };
    setScrollDragAtivo(true);
  }

  function moverScrollDrag(e: React.MouseEvent<HTMLDivElement>) {
    if (!scrollDrag.current.ativo) return;
    const el = scrollRef.current;
    if (!el) return;
    el.scrollLeft = scrollDrag.current.scrollLeft - (e.pageX - scrollDrag.current.startX);
  }

  function encerrarScrollDrag() {
    scrollDrag.current.ativo = false;
    setScrollDragAtivo(false);
  }

  // Pedido pendente de confirmação ao soltar em coluna com motivo obrigatório.
  const [modalPerda, setModalPerda] = useState<{ code: string; statusAlvo: OrderStatus } | null>(
    null,
  );
  const [motivoPerda, setMotivoPerda] = useState('');

  // Modal de exclusão
  const [modalExcluir, setModalExcluir] = useState<{ code: string } | null>(null);

  // Modal de criação — URL ?novo=1&nome=X&fone=Y abre o modal diretamente (extensão WhatsApp)
  const [searchParams, setSearchParams] = useSearchParams();
  const novoViaUrl = searchParams.get('novo') === '1';
  const nomeParam = searchParams.get('nome') ?? '';
  const foneParam = searchParams.get('fone') ?? '';
  const [modalAberto, setModalAberto] = useState(false);
  const modalCriar = novoViaUrl || modalAberto;

  function fecharModalCriar() {
    if (novoViaUrl) {
      setSearchParams({}, { replace: true });
    } else {
      setModalAberto(false);
    }
  }

  const mover = useMutation({
    mutationFn: ({
      code,
      status,
      lostReason,
    }: {
      code: string;
      status: OrderStatus;
      lostReason?: string;
    }) => api.pedidos.mudarStatus(code, { status, lostReason }),
    onSuccess: () => {
      toast.success('Pedido movido.');
      void queryClient.invalidateQueries({ queryKey: ['pedidos'] });
    },
    onError: (erro: Error) => toast.error(erro.message),
  });

  const excluir = useMutation({
    mutationFn: (code: string) => api.pedidos.excluir(code),
    onSuccess: () => {
      toast.success('Pedido excluído.');
      setModalExcluir(null);
      void queryClient.invalidateQueries({ queryKey: ['pedidos'] });
    },
    onError: (erro: Error) => toast.error(erro.message),
  });

  function handleDrop(colunaKey: string) {
    setColunaAlvo(null);
    if (!arrastando) return;

    const def = KANBAN_COLUMNS.find((c) => c.key === colunaKey);
    if (!def) return;

    const targetStatus = def.statuses[0] as OrderStatus;

    if (COLUNAS_COM_MOTIVO.has(colunaKey)) {
      setModalPerda({ code: arrastando, statusAlvo: targetStatus });
      setMotivoPerda('');
      setArrastando(null);
      return;
    }

    mover.mutate({ code: arrastando, status: targetStatus });
    setArrastando(null);
  }

  function confirmarPerda() {
    if (!modalPerda || !motivoPerda.trim()) return;
    mover.mutate({
      code: modalPerda.code,
      status: modalPerda.statusAlvo,
      lostReason: motivoPerda.trim(),
    });
    setModalPerda(null);
  }

  return (
    <>
      <PageHeader
        title="Pedidos"
        description="Controle interno de pedidos do WhatsApp"
        actions={
          <div className="flex items-center gap-3">
            {can('pedidos:editar') ? (
              <Button size="sm" onClick={() => setModalAberto(true)}>
                <Plus className="size-4" />
                Novo pedido
              </Button>
            ) : null}
            <Link
              to="/pedidos/lista"
              className="text-brand-700 text-sm font-semibold hover:underline"
            >
              Ver como lista
            </Link>
          </div>
        }
      />

      {recuperacao.data && recuperacao.data.items.length > 0 ? (
        <div className="mb-4 flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 p-3">
          <AlertTriangle className="mt-0.5 size-5 shrink-0 text-amber-600" aria-hidden />
          <div className="text-sm">
            <p className="font-semibold text-amber-900">
              {recuperacao.data.items.length} pedido(s) sem resposta há mais de{' '}
              {recuperacao.data.horas} h
            </p>
            <p className="mt-0.5 text-amber-800">
              {recuperacao.data.items
                .slice(0, 5)
                .map((pedido) => pedido.code)
                .join(', ')}
              {recuperacao.data.items.length > 5 ? '…' : ''}
            </p>
          </div>
        </div>
      ) : null}

      {quadro.isLoading ? (
        <Spinner label="Carregando o quadro…" />
      ) : (
        <>
          <div
            ref={scrollRef}
            className={cn(
              'flex gap-3 overflow-x-auto pb-4 select-none',
              scrollDragAtivo ? 'cursor-grabbing' : 'cursor-grab',
            )}
            onMouseDown={iniciarScrollDrag}
            onMouseMove={moverScrollDrag}
            onMouseUp={encerrarScrollDrag}
            onMouseLeave={encerrarScrollDrag}
          >
            {quadro.data?.colunas.map((coluna) => {
              const expandida = colunasExpandidas.has(coluna.key);
              const itensVisiveis = expandida
                ? coluna.items
                : coluna.items.slice(0, MAX_CARDS_COLAPSADO);
              const temMais = coluna.items.length > MAX_CARDS_COLAPSADO;

              return (
                <section
                  key={coluna.key}
                  className={cn(
                    'w-72 shrink-0 rounded-lg transition-colors duration-150',
                    arrastando && colunaAlvo === coluna.key && 'bg-brand-50 ring-brand-400 ring-2',
                  )}
                  onDragOver={(e) => {
                    e.preventDefault();
                    if (arrastando) setColunaAlvo(coluna.key);
                  }}
                  onDragLeave={(e) => {
                    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                      setColunaAlvo(null);
                    }
                  }}
                  onDrop={() => handleDrop(coluna.key)}
                >
                  <header className="mb-2 flex items-center justify-between px-1">
                    <h2 className="text-ink-800 text-sm font-bold">{coluna.label}</h2>
                    <span className="bg-ink-200 text-ink-700 rounded-full px-2 py-0.5 text-xs font-semibold">
                      {coluna.items.length}
                    </span>
                  </header>

                  <div className="space-y-2">
                    {coluna.items.length === 0 ? (
                      <p
                        className={cn(
                          'border-ink-300 text-ink-400 rounded-lg border border-dashed px-3 py-6 text-center text-xs transition-colors',
                          arrastando &&
                            colunaAlvo === coluna.key &&
                            'border-brand-400 text-brand-500',
                        )}
                      >
                        Solte aqui
                      </p>
                    ) : (
                      itensVisiveis.map((pedido) => (
                        <CardDoPedido
                          key={pedido.code}
                          pedido={pedido}
                          dragging={arrastando === pedido.code}
                          onDragStart={() => setArrastando(pedido.code)}
                          onDragEnd={() => {
                            setArrastando(null);
                            setColunaAlvo(null);
                          }}
                          onExcluir={
                            can('pedidos:editar')
                              ? () => setModalExcluir({ code: pedido.code })
                              : undefined
                          }
                        />
                      ))
                    )}

                    {temMais ? (
                      <button
                        type="button"
                        onClick={() => toggleColuna(coluna.key)}
                        className="border-ink-300 text-ink-500 hover:border-brand-400 hover:text-brand-600 flex w-full items-center justify-center gap-1 rounded-lg border border-dashed px-3 py-2 text-xs transition-colors"
                      >
                        {expandida ? (
                          <>
                            <ChevronUp className="size-3" aria-hidden />
                            Ver menos
                          </>
                        ) : (
                          <>
                            <ChevronDown className="size-3" aria-hidden />+{' '}
                            {coluna.items.length - MAX_CARDS_COLAPSADO} mais
                          </>
                        )}
                      </button>
                    ) : null}
                  </div>
                </section>
              );
            })}
          </div>

          <p className="text-ink-400 flex items-center gap-1.5 text-xs">
            <RefreshCw
              className={quadro.isFetching ? 'size-3 animate-spin' : 'size-3'}
              aria-hidden
            />
            Atualiza sozinho a cada {INTERVALO_MS / 1000} segundos
            {quadro.data ? ` · última leitura ${formatDateTime(quadro.data.atualizadoEm)}` : ''}
          </p>
        </>
      )}

      {/* Modal: mover para coluna com motivo obrigatório */}
      <Modal
        open={modalPerda !== null}
        onClose={() => setModalPerda(null)}
        title={`Mover para ${modalPerda ? ORDER_STATUS_LABELS[modalPerda.statusAlvo] : ''}`}
        footer={
          <>
            <Button variant="outline" onClick={() => setModalPerda(null)}>
              Cancelar
            </Button>
            <Button onClick={confirmarPerda} disabled={!motivoPerda.trim() || mover.isPending}>
              Confirmar
            </Button>
          </>
        }
      >
        <Field label="Motivo" hint="Obrigatório. Aparece no relatório de vendas perdidas." required>
          <Input
            value={motivoPerda}
            onChange={(e) => setMotivoPerda(e.target.value)}
            placeholder="Ex.: achou mais barato / cliente desistiu"
            autoFocus
          />
        </Field>
      </Modal>

      {/* Modal: confirmar exclusão */}
      <Modal
        open={modalExcluir !== null}
        onClose={() => setModalExcluir(null)}
        title="Excluir pedido"
        footer={
          <>
            <Button variant="outline" onClick={() => setModalExcluir(null)}>
              Cancelar
            </Button>
            <Button
              variant="danger"
              onClick={() => modalExcluir && excluir.mutate(modalExcluir.code)}
              disabled={excluir.isPending}
            >
              {excluir.isPending ? 'Excluindo…' : 'Excluir'}
            </Button>
          </>
        }
      >
        <p className="text-sm">
          Deseja excluir permanentemente o pedido <strong>{modalExcluir?.code}</strong>? Esta ação
          não pode ser desfeita.
        </p>
      </Modal>

      {/* Modal: criar pedido */}
      {modalCriar ? (
        <ModalCriarPedido
          initialName={nomeParam}
          initialPhone={foneParam}
          onClose={fecharModalCriar}
          onCriado={() => {
            fecharModalCriar();
            void queryClient.invalidateQueries({ queryKey: ['pedidos'] });
          }}
        />
      ) : null}
    </>
  );
}

function CardDoPedido({
  pedido,
  dragging,
  onDragStart,
  onDragEnd,
  onExcluir,
}: {
  pedido: OrderCard;
  dragging: boolean;
  onDragStart: () => void;
  onDragEnd: () => void;
  onExcluir?: () => void;
}) {
  return (
    <div
      draggable
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = 'move';
        onDragStart();
      }}
      onDragEnd={onDragEnd}
      onMouseDown={(e) => e.stopPropagation()}
      className={cn('cursor-grab active:cursor-grabbing', dragging && 'opacity-40')}
    >
      <div className="border-ink-200 hover:border-brand-500 relative block rounded-lg border bg-white p-3 shadow-sm transition-colors">
        {onExcluir ? (
          <button
            type="button"
            aria-label="Excluir pedido"
            onClick={(e) => {
              e.stopPropagation();
              onExcluir();
            }}
            className="text-ink-300 absolute top-2 right-2 rounded p-0.5 transition-colors hover:text-red-500"
          >
            <Trash2 className="size-3.5" aria-hidden />
          </button>
        ) : null}

        <Link to={`/pedidos/${pedido.code}`} draggable={false} className="block">
          <div className="flex items-start justify-between gap-2 pr-4">
            <span className="text-ink-900 text-sm font-bold">{pedido.code}</span>
            {pedido.atrasado ? (
              <span
                title="Sem resposta há mais de 24 h"
                className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[0.625rem] font-bold text-amber-800"
              >
                parado
              </span>
            ) : null}
          </div>

          <p className="text-ink-700 mt-1 truncate text-sm">{pedido.name}</p>
          <p className="text-ink-400 text-xs">{pedido.deliveryLabel}</p>

          <div className="mt-2 flex items-end justify-between">
            <span className="text-ink-900 font-bold">
              {formatBRL(pedido.finalValue ?? pedido.total)}
              {pedido.finalValue !== null ? (
                <span className="text-ink-400 ml-1 text-xs font-normal">fechado</span>
              ) : null}
            </span>

            {pedido.whatsappClickedAt ? (
              <MessageCircle className="size-4 text-emerald-600" aria-label="Abriu o WhatsApp" />
            ) : null}
          </div>

          <p className="text-ink-400 mt-1 text-[0.625rem]">{formatDateTime(pedido.createdAt)}</p>
        </Link>
      </div>
    </div>
  );
}

interface ItemLinha {
  name: string;
  variantName: string;
  qty: string;
  unitPrice: string;
}

function mascaraTelefone(valor: string): string {
  const nums = valor.replace(/\D/g, '').slice(0, 11);
  if (nums.length <= 2) return nums.length ? `(${nums}` : '';
  if (nums.length <= 6) return `(${nums.slice(0, 2)}) ${nums.slice(2)}`;
  if (nums.length <= 10) return `(${nums.slice(0, 2)}) ${nums.slice(2, 6)}-${nums.slice(6)}`;
  return `(${nums.slice(0, 2)}) ${nums.slice(2, 7)}-${nums.slice(7)}`;
}

function ModalCriarPedido({
  initialName = '',
  initialPhone = '',
  onClose,
  onCriado,
}: {
  initialName?: string;
  initialPhone?: string;
  onClose: () => void;
  onCriado: () => void;
}) {
  const toast = useToast();

  const metodosQuery = useQuery({
    queryKey: ['pedidos', 'metodosEntrega'],
    queryFn: api.pedidos.metodosEntrega,
  });

  const [name, setName] = useState(initialName);
  const [phone, setPhone] = useState(() => mascaraTelefone(initialPhone));
  const [deliveryMethodId, setDeliveryMethodId] = useState('');
  const [deliveryNote, setDeliveryNote] = useState('');
  const [notesCustomer, setNotesCustomer] = useState('');
  const [source, setSource] = useState<'web' | 'instagram' | 'google' | 'direto'>('direto');
  const [items, setItems] = useState<ItemLinha[]>([
    { name: '', variantName: '', qty: '1', unitPrice: '' },
  ]);

  const metodoSelecionado: DeliveryMethodOption | undefined = metodosQuery.data?.find(
    (m) => m.id === deliveryMethodId,
  );

  function setItem(idx: number, campo: keyof ItemLinha, valor: string) {
    setItems((prev) => prev.map((item, i) => (i === idx ? { ...item, [campo]: valor } : item)));
  }

  function adicionarItem() {
    setItems((prev) => [...prev, { name: '', variantName: '', qty: '1', unitPrice: '' }]);
  }

  function removerItem(idx: number) {
    setItems((prev) => prev.filter((_, i) => i !== idx));
  }

  const criar = useMutation({
    mutationFn: () =>
      api.pedidos.criar({
        name: name.trim(),
        phone: phone.trim(),
        deliveryMethodId,
        deliveryNote: deliveryNote.trim() || undefined,
        notesCustomer: notesCustomer.trim() || undefined,
        source,
        items: items.map((item) => ({
          name: item.name.trim(),
          variantName: item.variantName.trim() || undefined,
          qty: Number(item.qty),
          unitPrice: Math.round(parseFloat(item.unitPrice.replace(',', '.')) * 100) || 0,
        })),
      }),
    onSuccess: (pedido) => {
      toast.success('Pedido criado.');
      // Avisa a extensão (content script nesta mesma página) para etiquetar a
      // conversa no WhatsApp. postMessage na própria aba — não usa window.opener,
      // que o WhatsApp Web corta via COOP.
      window.postMessage(
        { type: 'CP_PEDIDO_CRIADO', code: pedido.code, phone: phone.trim() },
        window.location.origin,
      );
      onCriado();
    },
    onError: (erro: Error) => toast.error(erro.message),
  });

  const podeSalvar =
    name.trim() &&
    phone.trim() &&
    deliveryMethodId &&
    items.length > 0 &&
    items.every(
      (i) =>
        i.name.trim() &&
        i.variantName.trim() &&
        Number(i.qty) > 0 &&
        parseFloat(i.unitPrice.replace(',', '.')) > 0,
    );

  return (
    <Modal
      open
      onClose={onClose}
      title="Novo pedido"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={() => criar.mutate()} disabled={!podeSalvar || criar.isPending}>
            {criar.isPending ? 'Criando…' : 'Criar pedido'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Nome do cliente" required>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex.: Maria Silva"
              autoFocus
            />
          </Field>
          <Field label="WhatsApp" required>
            <Input
              value={phone}
              onChange={(e) => setPhone(mascaraTelefone(e.target.value))}
              placeholder="(11) 99999-9999"
              inputMode="tel"
            />
          </Field>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Forma de entrega" required>
            {metodosQuery.isLoading ? (
              <Spinner />
            ) : (
              <Select
                value={deliveryMethodId}
                onChange={(e) => setDeliveryMethodId(e.target.value)}
              >
                <option value="">Selecione…</option>
                {metodosQuery.data?.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.label}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label="Origem">
            <Select value={source} onChange={(e) => setSource(e.target.value as typeof source)}>
              <option value="direto">Direto (painel)</option>
              <option value="web">Site</option>
              <option value="instagram">Instagram</option>
              <option value="google">Google</option>
            </Select>
          </Field>
        </div>

        {metodoSelecionado?.asksAddress ? (
          <Field label="Endereço">
            <Input
              value={deliveryNote}
              onChange={(e) => setDeliveryNote(e.target.value)}
              placeholder="Ex.: Rua das Flores, 123 – Jardim Primavera"
            />
          </Field>
        ) : null}

        <Field label="Observação do cliente">
          <Textarea
            rows={2}
            value={notesCustomer}
            onChange={(e) => setNotesCustomer(e.target.value)}
            placeholder="Algo que o cliente pediu para considerar…"
          />
        </Field>

        <div>
          <p className="text-ink-700 mb-2 text-sm font-semibold">
            Produtos <span className="text-red-500">*</span>
          </p>
          <div className="space-y-2">
            <div className="hidden grid-cols-[1fr_auto] gap-2 sm:grid">
              <div className="grid gap-1.5 sm:grid-cols-[2fr_1fr_1fr_1fr]">
                <span className="text-ink-500 text-xs">Nome do produto <span className="text-red-500">*</span></span>
                <span className="text-ink-500 text-xs">Cor / tamanho <span className="text-red-500">*</span></span>
                <span className="text-ink-500 text-xs">Qtd <span className="text-red-500">*</span></span>
                <span className="text-ink-500 text-xs">R$ unit. <span className="text-red-500">*</span></span>
              </div>
              <span className="w-4" />
            </div>
            {items.map((item, idx) => (
              <div key={idx} className="grid grid-cols-[1fr_auto] gap-2">
                <div className="grid gap-1.5 sm:grid-cols-[2fr_1fr_1fr_1fr]">
                  <Input
                    value={item.name}
                    onChange={(e) => setItem(idx, 'name', e.target.value)}
                    placeholder="Nome do produto"
                  />
                  <Input
                    value={item.variantName}
                    onChange={(e) => setItem(idx, 'variantName', e.target.value)}
                    placeholder="Cor / tamanho"
                  />
                  <Input
                    value={item.qty}
                    onChange={(e) => setItem(idx, 'qty', e.target.value)}
                    placeholder="Qtd"
                    inputMode="decimal"
                  />
                  <Input
                    value={item.unitPrice}
                    onChange={(e) => setItem(idx, 'unitPrice', e.target.value)}
                    placeholder="R$ unit."
                    inputMode="decimal"
                  />
                </div>
                {items.length > 1 ? (
                  <button
                    type="button"
                    onClick={() => removerItem(idx)}
                    className="text-ink-400 mt-0.5 self-start hover:text-red-500"
                    aria-label="Remover item"
                  >
                    <Trash2 className="size-4" />
                  </button>
                ) : (
                  <span className="w-4" />
                )}
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={adicionarItem}
            className="text-brand-600 hover:text-brand-800 mt-2 flex items-center gap-1 text-sm font-medium"
          >
            <Plus className="size-3.5" />
            Adicionar produto
          </button>
        </div>
      </div>
    </Modal>
  );
}

/** A mesma informação em lista, para quem prefere filtrar e buscar. */
export function OrdersListPage() {
  const [params, setParams] = useSearchParams();

  const page = Number(params.get('page') ?? 1);
  const busca = params.get('busca') ?? '';
  const status = params.get('status') ?? '';

  const pedidos = useQuery({
    queryKey: ['pedidos', 'lista', { page, busca, status }],
    queryFn: () =>
      api.pedidos.lista({
        page,
        perPage: 30,
        busca: busca || undefined,
        status: status || undefined,
      }),
  });

  function ajustar(chave: string, valor: string) {
    const proximos = new URLSearchParams(params);
    if (valor) proximos.set(chave, valor);
    else proximos.delete(chave);
    // Mudar filtro volta para a primeira página: a página 4 do filtro
    // anterior quase nunca existe no novo.
    proximos.delete('page');
    setParams(proximos);
  }

  return (
    <>
      <PageHeader
        title="Pedidos"
        description="Busque por código, nome ou telefone."
        actions={
          <Link to="/pedidos" className="text-brand-700 text-sm font-semibold hover:underline">
            Ver como quadro
          </Link>
        }
      />

      <Card className="mb-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <Input
            placeholder="Código, nome ou telefone"
            defaultValue={busca}
            onKeyDown={(evento) => {
              if (evento.key === 'Enter') ajustar('busca', evento.currentTarget.value);
            }}
          />

          <Select value={status} onChange={(evento) => ajustar('status', evento.target.value)}>
            <option value="">Todos os status</option>
            {Object.entries(ORDER_STATUS_LABELS).map(([valor, rotulo]) => (
              <option key={valor} value={valor}>
                {rotulo}
              </option>
            ))}
          </Select>
        </div>
      </Card>

      {pedidos.isLoading ? (
        <Spinner />
      ) : pedidos.data && pedidos.data.items.length > 0 ? (
        <>
          <Card className="overflow-x-auto p-0">
            <table className="w-full text-sm">
              <thead className="bg-ink-50 text-ink-500 text-left text-xs uppercase">
                <tr>
                  <th className="px-3 py-2">Código</th>
                  <th className="px-3 py-2">Cliente</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Entrega</th>
                  <th className="px-3 py-2 text-right">Estimado</th>
                  <th className="px-3 py-2 text-right">Fechado</th>
                  <th className="px-3 py-2">Criado</th>
                </tr>
              </thead>
              <tbody className="divide-ink-200 divide-y">
                {pedidos.data.items.map((pedido) => (
                  <tr key={pedido.code} className="hover:bg-ink-50">
                    <td className="px-3 py-2 font-semibold">
                      <Link
                        to={`/pedidos/${pedido.code}`}
                        className="text-brand-700 hover:underline"
                      >
                        {pedido.code}
                      </Link>
                    </td>
                    <td className="px-3 py-2">
                      {pedido.name}
                      <span className="text-ink-400 block text-xs">{pedido.phone}</span>
                    </td>
                    <td className="px-3 py-2">{ORDER_STATUS_LABELS[pedido.status]}</td>
                    <td className="px-3 py-2">{pedido.deliveryLabel}</td>
                    <td className="px-3 py-2 text-right">{formatBRL(pedido.total)}</td>
                    <td className="px-3 py-2 text-right">
                      {pedido.finalValue === null ? '—' : formatBRL(pedido.finalValue)}
                    </td>
                    <td className="text-ink-500 px-3 py-2 text-xs">
                      {formatDateTime(pedido.createdAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>

          <Pagination
            page={pedidos.data.page}
            totalPages={pedidos.data.totalPages}
            total={pedidos.data.total}
            onChange={(proxima) => ajustar('page', String(proxima))}
          />
        </>
      ) : (
        <EmptyState title="Nenhum pedido com esses filtros." />
      )}
    </>
  );
}
